"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { format, parse } from "date-fns-jalali";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PhosphorIcon from "@/shared/components/common/PhosphorIcon";
import { createRoomsPattern } from "@/shared/libs/utils";
import type { HotelDetailV2BookingModel } from "../model/hotelDetailV2.model";
import { DateRangeModal } from "./BookingPanel";
import DetailAnchorTabs from "./DetailAnchorTabs";

interface DetailTopbarProps {
  booking: HotelDetailV2BookingModel;
  preserveUrlSearch: boolean;
  onStayChange: (
    search: Pick<SearchParams, "checkIn" | "checkOut" | "rooms">,
  ) => void;
  hotelName: string;
}

export default function DetailTopbar({
  booking,
  preserveUrlSearch,
  onStayChange,
  hotelName,
}: DetailTopbarProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [adultsOpen, setAdultsOpen] = useState(false);
  const adultsOpenedAtRef = useRef(0);
  const [draftCheckIn, setDraftCheckIn] = useState(booking.checkIn.replaceAll("-", "/"));
  const [draftCheckOut, setDraftCheckOut] = useState(booking.checkOut.replaceAll("-", "/"));
  const [draftAdults, setDraftAdults] = useState(booking.adults);
  const dateRangeLabel = formatMobileDateRange(draftCheckIn, draftCheckOut);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDraftCheckIn(booking.checkIn.replaceAll("-", "/"));
      setDraftCheckOut(booking.checkOut.replaceAll("-", "/"));
      setDraftAdults(booking.adults);
    }, 0);

    return () => window.clearTimeout(timeout);
  }, [booking.adults, booking.checkIn, booking.checkOut]);

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    window.location.assign("/hotel/");
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: hotelName, url: window.location.href });
        return;
      }

      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // Closing the native share sheet is not an actionable error.
    }
  };

  const replaceQuery = (changes: Record<string, string>) => {
    if (!preserveUrlSearch) {
      onStayChange({
        checkIn: changes.checkIn ?? booking.checkIn,
        checkOut: changes.checkOut ?? booking.checkOut,
        rooms: changes.rooms ?? booking.roomsParam,
      });
      window.dispatchEvent(new Event("hotel-detail-v2:rooms-loading"));
      return;
    }

    const params = new URLSearchParams(searchParams.toString());
    Object.entries(changes).forEach(([key, value]) => params.set(key, value));
    window.dispatchEvent(new Event("hotel-detail-v2:rooms-loading"));
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
  };

  const openAdultsDialog = () => {
    adultsOpenedAtRef.current = Date.now();
    setAdultsOpen(true);
  };

  const closeAdultsDialog = () => {
    setAdultsOpen(false);
  };

  const closeAdultsDialogFromBackdrop = () => {
    if (Date.now() - adultsOpenedAtRef.current < 400) return;
    closeAdultsDialog();
  };

  return (
    <div className="sticky top-0 z-40 border-b border-[#e5e7eb] bg-white/95 backdrop-blur">
      <div className="customContainer mx-auto flex h-12 items-center gap-3 max-md:!px-4 md:px-4">
        <button
          type="button"
          onClick={handleBack}
          className="flex shrink-0 items-center gap-1.5 text-sm font-medium text-[#6b7280] transition hover:text-primary"
        >
          <PhosphorIcon name="ArrowRight" size={15} />
          <span>بازگشت</span>
        </button>

        <DetailAnchorTabs className="hidden h-full flex-1 md:flex" />

        <div className="mr-auto grid min-w-0 flex-1 grid-cols-[minmax(0,1.25fr)_minmax(0,0.75fr)] gap-2 md:hidden">
          <button
            type="button"
            onClick={() => setCalendarOpen(true)}
            className="flex min-w-0 items-center gap-1.5 rounded-xl border border-[#e5e7eb] bg-white px-2.5 py-2 text-right text-xs font-bold text-[#1a1a2e]"
          >
            <PhosphorIcon name="CalendarBlank" size={14} className="shrink-0 text-[#6b7280]" />
            <span className="min-w-0 truncate">
              {dateRangeLabel}
            </span>
          </button>
          <button
            type="button"
            onPointerDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
              openAdultsDialog();
            }}
            onClick={openAdultsDialog}
            aria-label="انتخاب تعداد بزرگسال"
            className="flex min-w-0 items-center gap-1.5 rounded-xl border border-[#e5e7eb] bg-white px-2.5 py-2 text-right text-xs font-bold text-[#1a1a2e]"
          >
            <PhosphorIcon name="Users" size={14} className="shrink-0 text-[#6b7280]" />
            <span className="min-w-0 truncate">
              {draftAdults.toLocaleString("fa-IR")} بزرگسال
            </span>
          </button>
        </div>

        <div className="mr-auto hidden shrink-0 items-center gap-2 md:flex">
          <button
            type="button"
            onClick={() => setSaved((value) => !value)}
            aria-pressed={saved}
            aria-label={saved ? "حذف از علاقه‌مندی‌ها" : "افزودن به علاقه‌مندی‌ها"}
            title={saved ? "حذف از علاقه‌مندی‌ها" : "افزودن به علاقه‌مندی‌ها"}
            className={`grid h-8 w-8 place-items-center rounded-full border transition ${
              saved
                ? "border-primary/20 bg-primary/10 text-primary"
                : "border-[#e5e7eb] text-[#6b7280] hover:border-primary hover:text-primary"
            }`}
          >
            <PhosphorIcon name="Heart" size={15} weight={saved ? "fill" : "regular"} />
          </button>
          <button
            type="button"
            onClick={handleShare}
            aria-label="اشتراک‌گذاری"
            title={copied ? "لینک کپی شد" : "اشتراک‌گذاری"}
            className="grid h-8 w-8 place-items-center rounded-full border border-[#e5e7eb] text-[#6b7280] transition hover:border-primary hover:text-primary"
          >
            <PhosphorIcon name={copied ? "Check" : "ShareNetwork"} size={15} />
          </button>
        </div>
      </div>

      {calendarOpen ? (
        <DateRangeModal
          checkIn={draftCheckIn}
          checkOut={draftCheckOut}
          onClose={() => setCalendarOpen(false)}
          onConfirm={(checkIn, checkOut) => {
            setCalendarOpen(false);
            setDraftCheckIn(checkIn);
            setDraftCheckOut(checkOut);
            replaceQuery({
              checkIn: checkIn.replaceAll("/", "-"),
              checkOut: checkOut.replaceAll("/", "-"),
            });
          }}
        />
      ) : null}

      {adultsOpen ? (
        <MobileAdultsDialog
          adults={draftAdults}
          minAdults={booking.roomCount}
          onBackdropClose={closeAdultsDialogFromBackdrop}
          onClose={closeAdultsDialog}
          onConfirm={(adults) => {
            setDraftAdults(adults);
            setAdultsOpen(false);
            replaceQuery({
              rooms: createRoomsParamFromAdults(
                booking.roomsParam,
                adults,
                booking.children,
              ),
            });
          }}
        />
      ) : null}
    </div>
  );
}

function formatMobileDateRange(checkIn: string, checkOut: string) {
  return `${formatMobileDate(checkIn)} تا ${formatMobileDate(checkOut)}`;
}

function formatMobileDate(value: string) {
  try {
    const date = parse(value.replaceAll("-", "/"), "yyyy/MM/dd", new Date());
    return format(date, "d MMMM").replace(/\d+/g, (digits) =>
      Number(digits).toLocaleString("fa-IR"),
    );
  } catch {
    return value;
  }
}

function createRoomsParamFromAdults(
  currentRoomsParam: string,
  adults: number,
  children: number,
) {
  const rooms = createRoomsPattern("decode", currentRoomsParam || "2");
  const roomCount = Math.max(1, rooms.length);
  const baseAdult = Math.floor(adults / roomCount);
  const extraAdults = adults % roomCount;

  return createRoomsPattern(
    "encode",
    rooms.map((room, index) => ({
      ...room,
      adult: Math.max(1, baseAdult + (index < extraAdults ? 1 : 0)),
      child: index === 0 ? Array.from({ length: children }, () => "6") : [],
    })),
  );
}

function MobileAdultsDialog({
  adults,
  minAdults,
  onBackdropClose,
  onClose,
  onConfirm,
}: {
  adults: number;
  minAdults: number;
  onBackdropClose: () => void;
  onClose: () => void;
  onConfirm: (adults: number) => void;
}) {
  const [nextAdults, setNextAdults] = useState(adults);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return createPortal(
    <div className="fixed inset-0 z-[1000] grid place-items-end bg-[#6b728045] p-0 backdrop-blur-[1.5px] sm:place-items-center sm:p-4">
      <button
        aria-label="بستن انتخاب بزرگسال"
        className="absolute inset-0 cursor-default"
        onClick={onBackdropClose}
        type="button"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-adults-dialog-title"
        className="relative w-full rounded-t-2xl bg-white shadow-[0_24px_70px_rgba(15,23,42,0.28)] sm:max-w-md sm:rounded-2xl"
      >
        <header className="flex items-center justify-between border-b border-[#e5e7eb] px-5 py-4">
          <h3
            id="mobile-adults-dialog-title"
            className="text-base font-extrabold text-[#1a1a2e]"
          >
            تعداد بزرگسال
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="بستن"
            className="grid h-9 w-9 place-items-center rounded-full border border-[#e5e7eb] text-[#6b7280] transition hover:bg-[#f5f6f8]"
          >
            <PhosphorIcon name="X" size={16} />
          </button>
        </header>
        <div className="p-5">
          <div className="flex items-center justify-between gap-3 rounded-xl border border-[#e5e7eb] p-3">
            <div>
              <p className="text-sm font-bold text-[#1a1a2e]">بزرگسال</p>
              <p className="mt-0.5 text-xs text-[#6b7280]">
                حداقل {minAdults.toLocaleString("fa-IR")} نفر
              </p>
            </div>
            <div className="flex items-center overflow-hidden rounded-lg border border-[#e5e7eb]">
              <button
                type="button"
                onClick={() => setNextAdults((value) => Math.max(minAdults, value - 1))}
                disabled={nextAdults <= minAdults}
                className="grid h-9 w-9 place-items-center disabled:text-[#cbd5e1]"
              >
                <PhosphorIcon name="Minus" size={13} />
              </button>
              <span className="min-w-10 border-x border-[#e5e7eb] text-center text-sm font-extrabold">
                {nextAdults.toLocaleString("fa-IR")}
              </span>
              <button
                type="button"
                onClick={() => setNextAdults((value) => Math.min(12, value + 1))}
                disabled={nextAdults >= 12}
                className="grid h-9 w-9 place-items-center disabled:text-[#cbd5e1]"
              >
                <PhosphorIcon name="Plus" size={13} />
              </button>
            </div>
          </div>
        </div>
        <div className="border-t border-[#e5e7eb] p-4">
          <button
            type="button"
            onClick={() => onConfirm(nextAdults)}
            className="h-11 w-full rounded-xl bg-primary text-sm font-extrabold text-white transition hover:opacity-90"
          >
            تایید
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
